// bridgeCoachingNote — fires a Bardin Team line SMS to the recipient of a
// coaching note or reply, in addition to the in-app notification CoachingTab
// already inserts. Mirrors the dispatch Q&A bridge pattern: portal is the
// system of record, SMS is the buzz layer.
//
// Fire-and-forget. Errors are swallowed and logged via console.warn — never
// bubble up to the UI. Coaching threads keep working even if Twilio is down.
//
// The actual SMS goes out from the deployed `coaching-send-sms` edge function
// (in /supabase/functions/coaching-send-sms/index.ts). It hardcodes the FROM
// number to (470) 937-9490 — the Team line, NOT the customer line.

import { supabase } from "./supabase";

interface Args {
  thread_id: string;
  deal_id: string;
  recipient_user_id: string;
  sender_name: string;
  sender_user_id: string | null;
  body: string;
  /**
   * "note"    — first message of a thread (no @-tag for this recipient)
   * "reply"   — subsequent message (no @-tag for this recipient)
   * "mention" — recipient was @-tagged in this specific message; SMS bracket
   *             reads "X tagged you in a coaching note" so the buzz on the
   *             phone matches the in-portal `coaching_mention` notification.
   */
  kind: "note" | "reply" | "mention";
}

/**
 * Fire-and-forget. Caller should NOT await unless they need confirmation.
 * Returns nothing useful — all errors are swallowed.
 */
export async function bridgeCoachingNote(args: Args): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("coaching-send-sms", {
      body: args,
    });
    if (error) {
      console.warn("[bridgeCoachingNote] edge function error:", error.message ?? error);
      return;
    }
    if (data && typeof data === "object" && "skipped" in data) {
      // Recipient inactive or has no phone on file — both expected, not an error.
      console.info("[bridgeCoachingNote] skipped:", data.skipped);
    }
  } catch (err) {
    console.warn("[bridgeCoachingNote] unexpected error:", err);
  }
}
